import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const steps = [
  { num: '01', icon: 'FiMail', accent: '#289199', title: 'The Email-First Advantage', short: 'Bypass the noise. Hit the inbox.', body: 'We lead with your existing email list — clean, professional, and highly aligned. SMS blasting is noisy, spam-prone, and high-friction. We bypass all of that.' },
  { num: '02', icon: 'FiTarget', accent: '#eb5e55', title: 'You Call the Target', short: 'Deploy reviews where they hit hardest.', body: 'Your reviews need to be deployed where they have the most impact. Google, Facebook, Alignable, Zillow — any open platform.' },
  { num: '03', icon: 'FiShield', accent: '#f59e0b', title: 'The Built-In Shield', short: 'Public proof. Private clarity.', body: 'If a customer has a 1-to-3-star experience, that feedback is routed directly to you — privately. Your public reputation stays protected.' },
];

const outcomes = [
  { icon: 'FiTrendingUp', label: 'Rankings Boosted', sub: 'Fresh reviews = stronger local signals' },
  { icon: 'FiZap', label: 'Days, Not Months', sub: 'Immediate authority injection' },
  { icon: 'FiCpu', label: 'AI Systems Notice', sub: 'LLMs choose businesses with recent proof' },
  { icon: 'FiDollarSign', label: 'Trust → Revenue', sub: 'Dormant goodwill becomes a digital asset' },
];

function StepCard({ step, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: index * 0.1 }} className="group relative flex flex-col p-4 sm:p-5 rounded-2xl bg-[#181818] border border-white/5 overflow-hidden">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl" style={{ boxShadow: `inset 0 0 0 1px ${step.accent}40, 0 0 30px ${step.accent}10` }} />
      <div className="relative z-10 flex flex-col h-full gap-2">
        <div className="flex items-center justify-between">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${step.accent}20`, border: `1px solid ${step.accent}40` }}>
            <SafeIcon name={step.icon} className="h-4 w-4" style={{ color: step.accent }} />
          </div>
          <span className="text-3xl font-michroma text-white/5">{step.num}</span>
        </div>
        <span className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: step.accent }}>{step.short}</span>
        <h3 className="text-xs sm:text-sm font-michroma text-white leading-snug">{step.title}</h3>
        <AnimatePresence>
          {open && (
            <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="text-xs text-white/50 leading-relaxed overflow-hidden">{step.body}</motion.p>
          )}
        </AnimatePresence>
        <button onClick={() => setOpen(!open)} className="self-start flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest mt-auto" style={{ color: step.accent }}>
          {open ? 'Collapse' : 'Learn More'}
          <SafeIcon name={open ? 'FiChevronUp' : 'FiChevronDown'} className="h-3 w-3" />
        </button>
      </div>
    </motion.div>
  );
}

export default function SpeedRep() {
  return (
    <section className="relative w-full min-h-full bg-[#181818] flex flex-col justify-center py-24 px-4 sm:px-6 md:px-12 overflow-hidden font-lexend">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-teal/40 to-transparent" />
      
      <div className="relative z-10 max-w-6xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-5 sm:mb-8">
          <span className="inline-block text-brand-teal font-bold text-xs uppercase tracking-[0.2em] mb-3">Reactivation Campaign</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-michroma text-white tracking-tight leading-tight">
            The <span className="text-brand-teal drop-shadow-[0_0_20px_rgba(40,145,153,0.35)]">SpeedRep</span> Reactivation
          </h2>
          <p className="mt-3 text-white/55 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed">
            You are sitting on a goldmine of uncaptured energy. Past customers who already know, like, and trust you — <span className="text-white/80 font-semibold">We fix that.</span>
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {steps.map((step, i) => <StepCard key={i} step={step} index={i} />)}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="rounded-2xl border border-white/5 bg-[#202020] p-5 sm:p-6">
          <p className="text-center text-[10px] font-black uppercase tracking-[0.25em] text-brand-teal mb-5">The Outcome</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {outcomes.map((o, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <div className="h-10 w-10 rounded-xl bg-brand-teal/10 border border-brand-teal/20 flex items-center justify-center">
                  <SafeIcon name={o.icon} className="h-4 w-4 text-brand-teal" />
                </div>
                <div>
                  <p className="text-xs font-michroma text-white leading-tight">{o.label}</p>
                  <p className="text-[10px] text-white/40 mt-0.5 leading-snug">{o.sub}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-white/5 text-center">
            <p className="text-white/50 text-xs leading-relaxed max-w-xl mx-auto">
              Within days, you aren't just getting a few nice comments — you are actively altering how search algorithms and AI systems perceive your business. <span className="text-white/80">This is trust you've already earned, finally working for you.</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}